import React, { useState, useEffect } from 'react';
import { Button, Typography, Spin } from 'antd';
import { MediaPicker, MediaItem } from './MediaPicker';

const { Title } = Typography;

const styles = {
    mediaSelector: {
        width: '100%',
    },
    mediaPreviewContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
    },
    mediaPreview: {
        position: 'relative' as const,
        width: '100%',
        aspectRatio: '16/9',
        backgroundColor: 'var(--color-background-light)',
        overflow: 'hidden',
    },
    mediaContent: {
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const,
    },
    mediaInfo: {
        position: 'absolute' as const,
        bottom: 0,
        left: 0,
        right: 0,
        padding: '12px',
        background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
        color: 'white',
    },
    mediaInfoText: {
        display: 'block',
        color: 'white',
    },
    mediaInfoTextSecondary: {
        display: 'block',
        color: 'white',
        fontSize: '0.85em',
        opacity: 0.8,
    },
    emptyState: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        border: '2px dashed var(--color-border)',
        transition: 'all 0.3s ease',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    pickerContainer: {
        backgroundColor: 'var(--color-background-light)',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    title: {
        margin: 0,
        fontWeight: "bold",
    },
    cancelButton: {
        fontSize: '14px',
        padding: '4px 12px',
        height: 'auto',
    },
    content: {
    }
};

interface MediaSelectorProps {
    selectedMedia?: MediaItem;
    onMediaChange: (media: MediaItem) => void;
    categories: { value: string; label: string; }[];
    mediaItems: MediaItem[];
    compact: boolean;
    className: string;
    type?: 'image' | 'video' | 'all';
    onUpload?: (file: File) => Promise<MediaItem>;
    mediaPerRow?: number;
    isVisible?: boolean;
    onClose?: () => void;
    showCategories?: boolean;
    showSearch?: boolean;
    fetchMediaItems?: () => Promise<MediaItem[]>;
    uploadToBackend?: (file: File) => Promise<MediaItem>;
    isLoading?: boolean;
    error?: string;
}

export const MediaSelector: React.FC<MediaSelectorProps> = ({
    selectedMedia,
    onMediaChange,
    compact,
    categories,
    className,
    mediaItems: initialMediaItems,
    type = 'all',
    onUpload,
    mediaPerRow = 2,
    isVisible = false,
    onClose,
    showCategories = false,
    showSearch = false,
    fetchMediaItems,
    uploadToBackend,
    isLoading = false,
    error
}) => {
    const [mediaItems, setMediaItems] = useState<MediaItem[]>(initialMediaItems);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        setMediaItems(initialMediaItems);
    }, [initialMediaItems]);

    useEffect(() => {
        if (isVisible && fetchMediaItems) {
            fetchMediaItems().then(items => setMediaItems(items));
        }
    }, [isVisible, fetchMediaItems]);

    const handleSelect = (media: MediaItem) => {
        onMediaChange(media);
        onClose?.();
    };

    const handleUpload = async (file: File) => {
        if (!uploadToBackend) {
            if (onUpload) {
                return onUpload(file);
            }
            throw new Error('No upload handler provided');
        }
        
        setIsUploading(true);
        try {
            const uploadedMedia = await uploadToBackend(file);
            setMediaItems(prev => [...prev, uploadedMedia]);
            return uploadedMedia;
        } catch (err) {
            console.error('Upload failed:', err);
            throw err;
        } finally {
            setIsUploading(false);
        }
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div style={styles.pickerContainer} className={className}>
            <div style={styles.content}>
                {error && (
                    <div style={{ color: 'red', marginBottom: '16px' }}>
                        {error}
                    </div>
                )}
                <Spin spinning={isLoading || isUploading}>
                    <MediaPicker
                        compact={compact}
                        categories={categories}
                        mediaItems={mediaItems}
                        selectedMedia={selectedMedia}
                        onSelect={handleSelect}
                        onUpload={handleUpload}
                        type={type}
                        onBack={onClose}
                        showTitle={true}
                        mediaPerRow={mediaPerRow}
                        showCategories={showCategories}
                        showSearch={showSearch}
                        error={error}
                    />
                </Spin>
            </div>
        </div>
    );
}; 